// One stylesheet for both ParkConnect pages, injected as a <style> tag the way
// PhotoTrackerPage does it. Classes are `pc-` prefixed so nothing here can reach
// the pathfinder or the tour pages if they ever share a document.
//
// The board is deliberately plain: a single column of bordered rows, square-ish
// corners, no shadows, no hover lift. Someone checking whether a spot exists in
// their lot should be able to read the whole board in one pass — the only thing
// pulled out of the column is the price, because that is the other half of the
// decision.
//
// Motion is limited to view changes and feedback, never to reading the board.
// It is all transform/opacity, and switched off wholesale under
// prefers-reduced-motion at the bottom.

export const CSS = `
.pc-root {
  --pc-ink: #1a1a1a;
  --pc-ink-2: #555f6b;
  --pc-ink-3: #7d8794;
  --pc-line: #d6dbe1;
  --pc-line-2: #e8ebef;
  --pc-bg: #f4f5f7;
  --pc-card: #ffffff;
  --pc-blue: #1a5fb4;
  --pc-blue-soft: #e8f0fa;
  --pc-green: #146b32;
  --pc-green-soft: #e4f2e8;
  --pc-amber: #8a5a00;
  --pc-amber-soft: #fbf2dd;
  --pc-red: #b3261e;
  --pc-red-soft: #fbe9e7;
  --pc-ease: cubic-bezier(.3,.7,.4,1);

  background: var(--pc-bg);
  color: var(--pc-ink);
  font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
  min-height: 100dvh;
}
.pc-root *, .pc-root *::before, .pc-root *::after { box-sizing: border-box; }
.pc-spacer { flex: 1; }

/* ── Layout ───────────────────────────────────────────────────────────────── */

.pc-wrap { width: 100%; max-width: 720px; margin: 0 auto; padding: 0 16px; }
.pc-wrap.wide { max-width: 940px; }
.pc-main { padding: 20px 0 60px; }

.pc-h1 { font-size: 20px; font-weight: 700; letter-spacing: -.01em; margin: 0 0 4px; }
.pc-h2 { font-size: 16px; font-weight: 700; margin: 0 0 4px; }
.pc-lede { font-size: 14px; line-height: 1.55; color: var(--pc-ink-2); margin: 0; }
.pc-small { font-size: 13px; line-height: 1.5; color: var(--pc-ink-3); }

/* ── Header ───────────────────────────────────────────────────────────────── */

.pc-head { background: var(--pc-card); border-bottom: 1px solid var(--pc-line); }
.pc-head-in { display: flex; align-items: center; gap: 10px; min-height: 54px; flex-wrap: wrap; padding: 8px 0; }
.pc-brand {
  display: flex; align-items: center; gap: 7px;
  font-size: 16px; font-weight: 700; letter-spacing: -.01em;
  color: var(--pc-ink); text-decoration: none; flex: none;
}
.pc-brand .pc-mark { color: var(--pc-blue); }
.pc-brand-sub { color: var(--pc-blue); }
.pc-help {
  width: 24px; height: 24px; flex: none; border-radius: 50%;
  border: 1.5px solid var(--pc-line); background: var(--pc-card);
  color: var(--pc-ink-2); font: inherit; font-size: 13px; font-weight: 700;
  cursor: pointer; display: grid; place-items: center; line-height: 1;
  transition: background-color .15s var(--pc-ease), color .15s var(--pc-ease), border-color .15s var(--pc-ease);
}
.pc-help:hover { background: var(--pc-blue); border-color: var(--pc-blue); color: #fff; }
.pc-head-actions { display: flex; gap: 8px; margin-left: auto; }
@media (max-width: 520px) {
  .pc-head-actions { width: 100%; margin-left: 0; }
  .pc-head-actions .pc-btn { flex: 1; }
}

/* ── Buttons ──────────────────────────────────────────────────────────────── */

.pc-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  font: inherit; font-size: 14px; font-weight: 600;
  padding: 9px 14px; border-radius: 5px; border: 1px solid var(--pc-line);
  background: var(--pc-card); color: var(--pc-ink);
  cursor: pointer; text-decoration: none; white-space: nowrap;
  transition: background-color .15s var(--pc-ease), border-color .15s var(--pc-ease), color .15s var(--pc-ease);
}
.pc-btn:hover { background: #eef1f4; }
.pc-btn:active { background: #e4e8ec; }
.pc-btn:focus-visible { outline: 2px solid var(--pc-blue); outline-offset: 1px; }
.pc-btn:disabled { opacity: .5; cursor: not-allowed; }
.pc-btn.primary { background: var(--pc-blue); border-color: var(--pc-blue); color: #fff; }
.pc-btn.primary:hover { background: #164e93; border-color: #164e93; }
.pc-btn.sm { font-size: 13px; padding: 7px 11px; }
.pc-btn.tiny { font-size: 12px; padding: 5px 9px; gap: 5px; }
.pc-btn.block { width: 100%; }
.pc-btn.ok { color: var(--pc-green); border-color: #b6d9c2; background: var(--pc-green-soft); }
.pc-btn.no { color: var(--pc-red); border-color: #efc4c0; background: var(--pc-red-soft); }
.pc-btn.link {
  border: none; background: none; padding: 4px 0; color: var(--pc-blue);
  font-weight: 600; text-decoration: underline; text-underline-offset: 2px;
}
.pc-btn.link:hover { background: none; color: #164e93; }

/* ── Panels ───────────────────────────────────────────────────────────────── */

.pc-panel {
  background: var(--pc-card); border: 1px solid var(--pc-line);
  border-radius: 6px; padding: 16px;
}
.pc-panel + .pc-panel { margin-top: 12px; }

.pc-bar {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 10px 0 12px;
}

/* ── The board: one column, price pulled out ──────────────────────────────── */

.pc-list { border: 1px solid var(--pc-line); border-radius: 6px; background: var(--pc-card); overflow: hidden; }
.pc-item { display: flex; align-items: flex-start; gap: 16px; padding: 16px; }
.pc-item + .pc-item { border-top: 1px solid var(--pc-line-2); }
.pc-item-main { flex: 1; min-width: 0; }

/* The lot is a label, not a headline. A parker scanning the board is asking
   "when", not "where" — they already know which lots they can walk from — so the
   lot rides above the schedule at caption size and the hours carry the row. */
.pc-item-top { display: flex; align-items: center; gap: 6px; margin-bottom: 9px; }
.pc-lot-tag {
  font-size: 11.5px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase;
  color: var(--pc-ink-2); background: #eef1f4; border: 1px solid var(--pc-line);
  border-radius: 3px; padding: 2px 7px;
}
.pc-verified { color: var(--pc-green); }

/* The schedule: five day keys, then the window in the largest type on the row. */
.pc-strip { display: flex; gap: 4px; margin-bottom: 9px; }
.pc-strip-day {
  width: 30px; height: 27px; flex: none; border-radius: 4px;
  display: grid; place-items: center;
  font-size: 12.5px; font-weight: 700;
  border: 1px solid var(--pc-line-2); background: var(--pc-card); color: #b6bec8;
}
.pc-strip-day.on { background: var(--pc-blue); border-color: var(--pc-blue); color: #fff; }

.pc-when-row { display: flex; align-items: baseline; gap: 9px; flex-wrap: wrap; }
.pc-when-row + .pc-when-row { margin-top: 5px; }
.pc-when-days {
  font-size: 11.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase;
  color: var(--pc-ink-3); white-space: nowrap;
}
.pc-when-time {
  font-size: 19px; font-weight: 700; letter-spacing: -.015em; color: var(--pc-ink);
  line-height: 1.25;
}
.pc-when-time.all { color: var(--pc-green); }

.pc-item-note { margin: 9px 0 0; font-size: 13px; color: var(--pc-ink-3); }
.pc-item-side { flex: none; text-align: right; display: flex; flex-direction: column; align-items: flex-end; gap: 8px; }
.pc-item-price { font-size: 19px; font-weight: 700; white-space: nowrap; line-height: 1.2; }
.pc-item-price span { font-size: 12px; font-weight: 500; color: var(--pc-ink-3); display: block; }
.pc-pay {
  font-size: 11px; font-weight: 600; white-space: nowrap;
  border-radius: 3px; padding: 2px 7px; border: 1px solid transparent;
}
.pc-pay.monthly { background: #eef1f4; border-color: var(--pc-line); color: var(--pc-ink-2); }
.pc-pay.term { background: var(--pc-amber-soft); border-color: #ecd9a8; color: var(--pc-amber); }

@media (max-width: 480px) {
  .pc-item { flex-direction: column; gap: 12px; }
  .pc-item-side { align-items: center; text-align: left; width: 100%; flex-direction: row; }
  .pc-item-price { display: flex; align-items: baseline; gap: 6px; }
  .pc-item-price span { display: inline; }
  .pc-item-side .pc-btn { margin-left: auto; }
}

.pc-empty {
  border: 1px solid var(--pc-line); border-radius: 6px; background: var(--pc-card);
  padding: 34px 20px; text-align: center; color: var(--pc-ink-3);
}
.pc-empty h2 { color: var(--pc-ink); }

/* ── Forms ────────────────────────────────────────────────────────────────── */

.pc-field { display: block; margin-bottom: 14px; }
.pc-label { display: block; font-size: 13px; font-weight: 600; color: var(--pc-ink); margin-bottom: 5px; }
.pc-label .pc-opt { font-weight: 400; color: var(--pc-ink-3); }
.pc-hint { font-size: 12px; line-height: 1.5; color: var(--pc-ink-3); margin: 5px 0 0; }
.pc-input, .pc-select, .pc-textarea {
  width: 100%; font: inherit; font-size: 15px; color: var(--pc-ink);
  padding: 9px 10px; border: 1px solid var(--pc-line); border-radius: 5px;
  background: var(--pc-card); outline: none;
  transition: border-color .15s var(--pc-ease), box-shadow .15s var(--pc-ease);
}
.pc-textarea { min-height: 74px; resize: vertical; line-height: 1.5; }
.pc-select { appearance: none; -webkit-appearance: none; cursor: pointer;
  background-image: linear-gradient(45deg, transparent 50%, #7d8794 50%), linear-gradient(135deg, #7d8794 50%, transparent 50%);
  background-position: calc(100% - 17px) calc(50% + 1px), calc(100% - 12px) calc(50% + 1px);
  background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; padding-right: 32px; }
.pc-input:focus, .pc-select:focus, .pc-textarea:focus {
  border-color: var(--pc-blue); box-shadow: 0 0 0 2px rgba(26,95,180,.16);
}
.pc-input.plate { text-transform: uppercase; letter-spacing: .08em; font-weight: 600; }

/* The price is a monthly figure and nothing else, so the unit sits in the field
   rather than in a dropdown next to it. */
.pc-price-input { display: flex; align-items: center; gap: 8px; }
.pc-plan { display: flex; flex-direction: column; gap: 6px; }
.pc-plan .pc-toggle { align-self: flex-start; }
.pc-price-input .pc-input { max-width: 130px; }
.pc-price-prefix { font-size: 15px; font-weight: 700; color: var(--pc-ink-2); }
.pc-price-suffix { font-size: 14px; color: var(--pc-ink-2); }
.pc-row { display: grid; gap: 12px; grid-template-columns: 1fr 1fr; }
.pc-row.three { grid-template-columns: 1fr 1fr 1fr; }
@media (max-width: 560px) { .pc-row, .pc-row.three { grid-template-columns: 1fr; } }

.pc-fieldset { border: 1px solid var(--pc-line); border-radius: 6px; padding: 14px; margin: 0 0 14px; }
.pc-fieldset legend { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--pc-ink-3); padding: 0 6px; }

.pc-drop {
  display: flex; align-items: center; gap: 10px; width: 100%;
  border: 1px dashed var(--pc-line); border-radius: 5px; padding: 12px;
  background: #fafbfc; cursor: pointer; text-align: left; font: inherit;
  transition: border-color .15s var(--pc-ease), background-color .15s var(--pc-ease);
}
.pc-drop:hover { border-color: #a9b6c4; background: #f3f6f9; }
.pc-drop.over { border-color: var(--pc-blue); background: var(--pc-blue-soft); }
.pc-drop.done { border-style: solid; border-color: #b6d9c2; background: var(--pc-green-soft); }
.pc-drop-icon { color: var(--pc-ink-3); flex: none; }
.pc-drop.done .pc-drop-icon { color: var(--pc-green); }
.pc-drop-name { font-size: 14px; font-weight: 600; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pc-drop-sub { font-size: 12px; color: var(--pc-ink-3); }

.pc-toggle { display: inline-flex; border: 1px solid var(--pc-line); border-radius: 5px; overflow: hidden; }
.pc-toggle button {
  font: inherit; font-size: 13px; font-weight: 600; color: var(--pc-ink-2);
  border: none; background: var(--pc-card); padding: 7px 12px; cursor: pointer;
  transition: background-color .15s var(--pc-ease), color .15s var(--pc-ease);
}
.pc-toggle button + button { border-left: 1px solid var(--pc-line); }
.pc-toggle button.on { background: var(--pc-blue); color: #fff; }
.pc-toggle.sm button { font-size: 12px; padding: 5px 10px; }

.pc-note {
  display: flex; gap: 9px; align-items: flex-start;
  border-radius: 5px; padding: 10px 12px; font-size: 13px; line-height: 1.5;
  border: 1px solid transparent;
}
.pc-note.info { background: var(--pc-blue-soft); border-color: #cfe0f2; color: #14448f; }
.pc-note.warn { background: var(--pc-amber-soft); border-color: #ecd9a8; color: var(--pc-amber); }
.pc-note.bad { background: var(--pc-red-soft); border-color: #efc4c0; color: var(--pc-red); animation: pc-shake .3s var(--pc-ease); }
.pc-note.good { background: var(--pc-green-soft); border-color: #b6d9c2; color: var(--pc-green); }
.pc-note svg { flex: none; margin-top: 1px; }

/* The one thing on this site that must not be misread: we introduce two people
   and stop there. It is red, bordered and repeated on every surface where
   someone could form the opposite impression — the board, both forms, the
   confirmations and the help panel. */
.pc-note.red {
  background: var(--pc-red-soft); border-color: var(--pc-red); color: var(--pc-red);
  font-weight: 500;
}
.pc-note.red b { font-weight: 700; }
.pc-redline { color: var(--pc-red); font-size: 12.5px; line-height: 1.5; margin: 5px 0 0; font-weight: 600; }

/* ── Weekday picker ───────────────────────────────────────────────────────── */

/* Five fixed-width days rather than a stretched grid: M T W H F should read as
   a row of keys, not as five wide bars. */
.pc-week { display: flex; gap: 6px; }
.pc-wday {
  width: 44px; height: 40px; flex: none;
  border: 1px solid var(--pc-line); border-radius: 5px;
  background: var(--pc-card); font: inherit; font-size: 15px; font-weight: 700; color: var(--pc-ink-2);
  cursor: pointer; display: grid; place-items: center;
  transition: background-color .14s var(--pc-ease), color .14s var(--pc-ease), border-color .14s var(--pc-ease);
}
.pc-wday:hover { background: #eef1f4; }
.pc-wday.on { background: var(--pc-blue); border-color: var(--pc-blue); color: #fff; }
.pc-wday:focus-visible { outline: 2px solid var(--pc-blue); outline-offset: 1px; }

.pc-times { display: inline-flex; align-items: center; gap: 6px; color: var(--pc-ink-3); }
.pc-time {
  font: inherit; font-size: 13px; padding: 6px 7px; border: 1px solid var(--pc-line);
  border-radius: 5px; color: var(--pc-ink); background: var(--pc-card); outline: none;
}
.pc-time:focus { border-color: var(--pc-blue); }

.pc-slots { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
.pc-slot {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  border: 1px solid var(--pc-line-2); border-radius: 5px; padding: 7px 10px; background: #fafbfc;
}
.pc-slot-day { font-size: 13px; font-weight: 600; min-width: 76px; }
.pc-slot .pc-times { margin-left: auto; }

/* ── Board filter ─────────────────────────────────────────────────────────── */

.pc-filter { display: flex; align-items: center; gap: 6px; }
.pc-filter-day {
  width: 32px; height: 30px; flex: none;
  border: 1px solid var(--pc-line); border-radius: 4px; background: var(--pc-card);
  font: inherit; font-size: 13px; font-weight: 700; color: var(--pc-ink-2);
  cursor: pointer; display: grid; place-items: center;
  transition: background-color .14s var(--pc-ease), color .14s var(--pc-ease), border-color .14s var(--pc-ease);
}
.pc-filter-day:hover { background: #eef1f4; }
.pc-filter-day.on { background: var(--pc-blue); border-color: var(--pc-blue); color: #fff; }

/* ── View switching ───────────────────────────────────────────────────────── */

.pc-view { animation: pc-in .22s var(--pc-ease) both; }
.pc-back {
  display: inline-flex; align-items: center; gap: 6px; margin-bottom: 12px;
  font: inherit; font-size: 13px; font-weight: 600; color: var(--pc-ink-2);
  background: none; border: none; padding: 4px 0; cursor: pointer;
}
.pc-back:hover { color: var(--pc-blue); }
.pc-back svg { transform: rotate(180deg); }

/* ── Modal (help, documents) ──────────────────────────────────────────────── */

.pc-backdrop {
  position: fixed; inset: 0; z-index: 100; display: flex; align-items: center; justify-content: center;
  padding: 16px; background: rgba(26,26,26,.45);
  animation: pc-fade .16s var(--pc-ease) both;
}
.pc-modal {
  width: 100%; max-width: 460px; max-height: calc(100vh - 32px); overflow-y: auto;
  background: var(--pc-card); border-radius: 6px; border: 1px solid var(--pc-line);
  animation: pc-in .2s var(--pc-ease) both;
}
.pc-modal.wide { max-width: 760px; }
.pc-modal-head { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid var(--pc-line-2); }
.pc-modal-body { padding: 16px; }
.pc-modal-x {
  margin-left: auto; flex: none; border: 1px solid var(--pc-line); background: var(--pc-card);
  color: var(--pc-ink-2); width: 28px; height: 28px; border-radius: 5px;
  display: grid; place-items: center; cursor: pointer;
  transition: background-color .15s var(--pc-ease);
}
.pc-modal-x:hover { background: #eef1f4; }
.pc-steps { margin: 0; padding-left: 20px; font-size: 14px; line-height: 1.7; color: var(--pc-ink-2); }
.pc-steps li + li { margin-top: 8px; }
.pc-steps b { color: var(--pc-ink); }
.pc-doc-frame { width: 100%; height: 60vh; border: 1px solid var(--pc-line); border-radius: 5px; background: var(--pc-bg); }
.pc-doc-img { display: block; width: 100%; height: auto; border: 1px solid var(--pc-line); border-radius: 5px; }

/* ── Done panel ───────────────────────────────────────────────────────────── */

.pc-done { text-align: center; padding: 22px 6px; }
.pc-done .pc-check-circle { color: var(--pc-green); margin-bottom: 12px; }
.pc-done h2 { font-size: 17px; font-weight: 700; margin: 0 0 6px; }
.pc-done p { font-size: 14px; line-height: 1.6; color: var(--pc-ink-2); margin: 0 auto 16px; max-width: 42ch; }

/* ── Admin ────────────────────────────────────────────────────────────────── */

.pc-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
.pc-tab {
  font: inherit; font-size: 13px; font-weight: 600; color: var(--pc-ink-2);
  background: var(--pc-card); border: 1px solid var(--pc-line); border-radius: 5px;
  padding: 7px 12px; cursor: pointer;
  transition: background-color .15s var(--pc-ease), color .15s var(--pc-ease), border-color .15s var(--pc-ease);
}
.pc-tab:hover { background: #eef1f4; }
.pc-tab.on { background: var(--pc-ink); border-color: var(--pc-ink); color: #fff; }
.pc-tab-n { font-size: 11px; font-weight: 700; margin-left: 5px; opacity: .75; }

.pc-rows { display: flex; flex-direction: column; gap: 10px; }
.pc-row-top { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.pc-status {
  font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  padding: 2px 7px; border-radius: 3px; border: 1px solid transparent;
}
.pc-status.pending, .pc-status.new { background: var(--pc-amber-soft); border-color: #ecd9a8; color: var(--pc-amber); }
.pc-status.approved, .pc-status.connected { background: var(--pc-green-soft); border-color: #b6d9c2; color: var(--pc-green); }
.pc-status.rejected, .pc-status.closed { background: #eef1f4; border-color: var(--pc-line); color: var(--pc-ink-3); }
.pc-kv { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px 16px; margin-top: 12px; }
.pc-k { font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--pc-ink-3); }
.pc-v { font-size: 14px; margin-top: 2px; word-break: break-word; }
.pc-v a { color: var(--pc-blue); }
.pc-plate {
  display: inline-block; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px; font-weight: 700; letter-spacing: .1em;
  border: 1px solid var(--pc-line); border-radius: 4px; padding: 2px 7px; background: #fafbfc;
}
.pc-actions { display: flex; gap: 7px; flex-wrap: wrap; margin-top: 12px; padding-top: 11px; border-top: 1px solid var(--pc-line-2); }
.pc-pair { border-left: 3px solid var(--pc-blue); padding-left: 12px; margin-top: 12px; }

.pc-login { min-height: 100vh; min-height: 100dvh; display: grid; place-items: center; padding: 16px; }
.pc-login .pc-panel { width: 100%; max-width: 340px; }

/* ── Footer ───────────────────────────────────────────────────────────────── */

.pc-foot { border-top: 1px solid var(--pc-line); padding: 18px 0 40px; margin-top: 32px; }
.pc-foot-in { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.pc-foot a { color: var(--pc-ink-2); font-size: 13px; text-decoration: none; }
.pc-foot a:hover { color: var(--pc-blue); text-decoration: underline; }

/* ── Keyframes ────────────────────────────────────────────────────────────── */

@keyframes pc-spin { to { transform: rotate(360deg); } }
.pc-spin { animation: pc-spin .8s linear infinite; }
@keyframes pc-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes pc-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes pc-shake {
  0%, 100% { transform: translateX(0); }
  30% { transform: translateX(-4px); }
  60% { transform: translateX(3px); }
}
@keyframes pc-draw-ring { from { stroke-dashoffset: 140; } to { stroke-dashoffset: 0; } }
@keyframes pc-draw-tick { from { stroke-dashoffset: 34; } to { stroke-dashoffset: 0; } }
.pc-draw-ring { stroke-dasharray: 140; animation: pc-draw-ring .45s var(--pc-ease) both; }
.pc-draw-tick { stroke-dasharray: 34; animation: pc-draw-tick .3s var(--pc-ease) .38s both; }

@media (prefers-reduced-motion: reduce) {
  .pc-root *, .pc-root *::before, .pc-root *::after {
    animation-duration: .001ms !important; animation-delay: 0ms !important;
    transition-duration: .001ms !important;
  }
}
`;
